<script setup>
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CompanySortMenu from './components/CompanySortMenu.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  hasActiveFilters: { type: Boolean, default: false },
});

const emit = defineEmits(['search', 'update:sort', 'add', 'import', 'export', 'filter']);
</script>

<template>
  <header class="sticky top-0 z-10 px-6">
    <div
      class="flex items-start sm:items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl"
    >
      <span class="text-heading-1 truncate text-n-slate-12">
        {{ headerTitle }}
      </span>
      <div class="flex items-center flex-row flex-shrink-0 gap-3">
        <button
          data-filter-btn
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors flex-shrink-0"
          :class="hasActiveFilters
            ? 'bg-woot-500 text-white border-woot-500 hover:bg-woot-600'
            : 'text-n-slate-11 border-n-weak hover:bg-n-alpha-2'"
          @click="emit('filter')"
        >
          <Icon icon="i-lucide-list-filter" class="size-3.5" />
          Filter
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
          title="Import companies from CSV"
          @click="emit('import')"
        >
          <Icon icon="i-lucide-upload" class="size-3.5" />
          Import
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="emit('export')"
        >
          <Icon icon="i-lucide-download" class="size-3.5" />
          Export
        </button>

        <!-- Uiverse Add Company button -->
        <button class="cmp-Btn" @click="emit('add')">
          <div class="cmp-sign">+</div>
          <div class="cmp-text">Add Company</div>
        </button>

        <div class="flex items-center">
          <CompanySortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
        </div>
        <div v-if="showSearch" class="flex items-center gap-2 w-full">
          <Input
            :model-value="searchValue"
            type="search"
            :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="emit('search', $event.target.value)"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
      </div>
    </div>
  </header>
</template>

<style>
.cmp-Btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition-duration: 0.3s;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.199);
  background: linear-gradient(144deg, #af40ff, #5b42f3 50%, #00ddeb);
  flex-shrink: 0;
}

.cmp-sign {
  width: 100%;
  font-size: 1.4em;
  color: white;
  transition-duration: 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cmp-text {
  position: absolute;
  right: 0%;
  width: 0%;
  opacity: 0;
  color: white;
  font-size: 0.875em;
  font-weight: 500;
  transition-duration: 0.3s;
  white-space: nowrap;
}

.cmp-Btn:hover {
  width: 135px;
  transition-duration: 0.3s;
}

.cmp-Btn:hover .cmp-sign {
  width: 30%;
  transition-duration: 0.3s;
  padding-left: 15px;
}

.cmp-Btn:hover .cmp-text {
  opacity: 1;
  width: 70%;
  transition-duration: 0.3s;
  padding-right: 15px;
}

.cmp-Btn:active {
  transform: translate(2px, 2px);
}
</style>
